import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, XCircle, Activity, RefreshCw, TrendingUp, TrendingDown, Mail } from 'lucide-react';

const GET_AWS_STATUS = gql`
  query GetAWSStatus {
    getAWSStatus {
      sendQuota {
        max24HourSend
        maxSendRate
        sentLast24Hours
      }
      sendingEnabled
      reputationTracking {
        reputationTracking
        bounceRate
        complaintRate
      }
      stats {
        deliveryDelay
        bounces
        complaints
        deliveries
        rejects
      }
    }
  }
`;

export const AWSStatusView: React.FC = () => {
  const { data, loading, error, refetch } = useQuery(GET_AWS_STATUS, {
    fetchPolicy: 'cache-first',
    pollInterval: 60000 // Auto refresh every minute
  });

  const handleRefresh = () => {
    refetch();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-24 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load AWS SES status: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  const { sendQuota, sendingEnabled, reputationTracking, stats } = data?.getAWSStatus || {};

  const quotaUsagePercent = sendQuota 
    ? Math.round((sendQuota.sentLast24Hours / sendQuota.max24HourSend) * 100) 
    : 0;

  const getStatusBadge = (enabled: boolean) => {
    return enabled ? (
      <Badge variant="default" className="bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3 mr-1" />
        Enabled
      </Badge>
    ) : (
      <Badge variant="destructive">
        <XCircle className="h-3 w-3 mr-1" />
        Disabled
      </Badge>
    );
  };

  const getReputationBadge = (rate: number, threshold: number) => {
    if (rate <= threshold * 0.5) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Good</Badge>;
    } else if (rate <= threshold * 0.8) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Warning</Badge>;
    } else {
      return <Badge variant="destructive">Critical</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold">AWS SES Status Dashboard</h3>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Main Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Service Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Sending Status:</span>
            {getStatusBadge(sendingEnabled)}
          </div>
        </CardContent>
      </Card>

      {/* Quota and Limits */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">24-Hour Send Quota</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-3xl font-bold text-blue-600">
              {sendQuota?.sentLast24Hours?.toLocaleString() || 0}
            </div>
            <div className="text-sm text-gray-600">
              of {sendQuota?.max24HourSend?.toLocaleString() || 0} emails
            </div>
            <Progress value={quotaUsagePercent} className="h-2" />
            <div className="text-xs text-gray-500">
              {quotaUsagePercent}% used
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Send Rate Limit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {sendQuota?.maxSendRate || 0}
            </div>
            <div className="text-sm text-gray-600">emails per second</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Deliveries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {stats?.deliveries?.toLocaleString() || 0}
            </div>
            <div className="text-sm text-gray-600">successful deliveries</div>
          </CardContent>
        </Card>
      </div>

      {/* Reputation Tracking */}
      <Card>
        <CardHeader>
          <CardTitle>Reputation Tracking</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Tracking Enabled</span>
                {getStatusBadge(reputationTracking?.reputationTracking)}
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Bounce Rate</span>
                {getReputationBadge(reputationTracking?.bounceRate * 100 || 0, 5)}
              </div>
              <div className="text-lg font-semibold">
                {((reputationTracking?.bounceRate || 0) * 100).toFixed(2)}%
              </div>
              <div className="text-xs text-gray-500">Threshold: &lt; 5%</div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Complaint Rate</span>
                {getReputationBadge(reputationTracking?.complaintRate * 100 || 0, 1)}
              </div>
              <div className="text-lg font-semibold">
                {((reputationTracking?.complaintRate || 0) * 100).toFixed(2)}%
              </div>
              <div className="text-xs text-gray-500">Threshold: &lt; 0.1%</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Sending Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 flex items-center justify-center gap-1">
                <TrendingUp className="h-5 w-5" />
                {stats?.deliveries?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600">Deliveries</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 flex items-center justify-center gap-1">
                <TrendingDown className="h-5 w-5" />
                {stats?.bounces?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600">Bounces</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 flex items-center justify-center gap-1">
                <XCircle className="h-5 w-5" />
                {stats?.complaints?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600">Complaints</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600 flex items-center justify-center gap-1">
                <AlertCircle className="h-5 w-5" />
                {stats?.rejects?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600">Rejects</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-gray-500 text-center">
        Auto-refreshes every minute • Last updated: {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
};